import React, { type MouseEvent, type SyntheticEvent, useCallback } from 'react';
import { SmartCardProvider as Provider } from '@atlaskit/link-provider';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import { useEmbeddedPageTracking } from '@atlassian/jira-issue-create-confluence-content/src/controllers/use-embedded-page-tracking';
import type { ConfluencePage, FailedRemoteLink } from '@atlassian/jira-issue-shared-types';
import { APPLINK_REQ_AUTH, REMOTE_LINK_MISSING } from '@atlassian/jira-issue-view-common-constants';
import {
	useSmartCardProductType,
	useSmartCardIsAIEnabled,
} from '@atlassian/jira-linking-platform-utils';
import { useProjectContext } from '@atlassian/jira-providers-project-context';
import type { Href, RemoteLinkGlobalId } from '@atlassian/jira-shared-types';
import SmartLinkCard from '../../../smart-link-content';
import { JiraSmartLinkLocations } from '../../../smart-link-content/smart-link-analytics';
import { useConfirmationModalState } from '../../controllers/use-confirmation-modal-state';
import { getContentWithHostname, shouldLoadConfluenceInNewTab } from '../../utils';
import { ConfirmationModal } from '../confirmation-modal/confirmation-modal';
import messages from './messages';

type Props = {
	groupId: string;
	pages: (ConfluencePage | FailedRemoteLink)[];
	onClick?: (
		event: MouseEvent<HTMLElement, globalThis.MouseEvent> | SyntheticEvent<Element, Event>,
	) => void;
	onAuthenticateApplink: (href: Href) => void;
	onDeleteConfluencePageLink: (id: RemoteLinkGlobalId) => void;
	systemConfluenceAppLinkUrl: string | undefined;
};

const isPageError = (page: ConfluencePage | FailedRemoteLink): page is FailedRemoteLink =>
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	(page as FailedRemoteLink).error !== undefined;

export const ConfluencePageLineCardGroup = ({
	pages,
	onClick,
	onAuthenticateApplink,
	onDeleteConfluencePageLink,
	systemConfluenceAppLinkUrl,
}: Props) => {
	const { formatMessage } = useIntl();
	const trackEmbeddedConfluence = useEmbeddedPageTracking();
	const projectContext = useProjectContext();
	const { onDeleteClick, onConfirmDelete, onCancelDelete, modalOpen } = useConfirmationModalState(
		onDeleteConfluencePageLink,
	);
	const currentHostname = window?.location?.hostname || '';
	const isLinkedPagesBugFixEnabled = ff('linked-pages-embedded-bug-fix');
	const isAppLinkAuthError = useCallback(
		(page: ConfluencePage | FailedRemoteLink): page is FailedRemoteLink =>
			isPageError(page) &&
			page.error === APPLINK_REQ_AUTH &&
			page.repairLink?.href !== undefined &&
			onAuthenticateApplink !== undefined,
		[onAuthenticateApplink],
	);
	const isRestrictedPageError = useCallback(
		(page: ConfluencePage | FailedRemoteLink): page is FailedRemoteLink =>
			isPageError(page) && page.error === REMOTE_LINK_MISSING && page.link?.href !== undefined,
		[],
	);
	const isHandleRestrictedPageOnClickEnabled = ff(
		'linked_mentioned_pages_handle_restricted_page_on_click',
	);

	const smartCardProviderProduct = useSmartCardProductType();
	const isAIFeatureEnabled = useSmartCardIsAIEnabled();

	/**
	 * Returns either the url contained in the link or the authentication url for a failed applink
	 * @param page is the confluence page to be connected
	 */
	const getHrefForSmartCard = useCallback(
		(page: ConfluencePage | FailedRemoteLink) => {
			if (
				isAppLinkAuthError(page) ||
				(isHandleRestrictedPageOnClickEnabled && isRestrictedPageError(page))
			) {
				return page.repairLink ? page.repairLink.href : page.link.href;
			}

			return isPageError(page) ? page.link.href : page.href;
		},
		[isAppLinkAuthError, isRestrictedPageError, isHandleRestrictedPageOnClickEnabled],
	);

	const getPageWithHostname = useCallback(
		(page: ConfluencePage | FailedRemoteLink) => {
			if (isPageError(page)) {
				return page;
			}
			return getContentWithHostname({
				content: page,
				systemConfluenceAppLinkUrl,
				currentHostname,
			});
		},
		[currentHostname, systemConfluenceAppLinkUrl],
	);

	const loadConfluenceInNewTab = useCallback(
		(page: ConfluencePage | FailedRemoteLink) => {
			if (isPageError(page)) {
				return false;
			}
			return shouldLoadConfluenceInNewTab({
				content: page,
				systemConfluenceAppLinkUrl,
				currentHostname,
			});
		},
		[currentHostname, systemConfluenceAppLinkUrl],
	);

	/**
	 * Returns an authenticate function for failed applinks or a passed onClick function for all other cases
	 * @param page is the confluence page to be connected
	 */
	const onClickSmartCard = useCallback(
		(page: ConfluencePage | FailedRemoteLink) => {
			if (isAppLinkAuthError(page)) {
				return onAuthenticateApplink(getHrefForSmartCard(page));
			}
			if (isHandleRestrictedPageOnClickEnabled && isRestrictedPageError(page)) {
				// open the page in a new tab as restricted pages don't open in embedded confluence
				window.open(getHrefForSmartCard(page), '_blank', 'noopener, noreferrer');
			}
			return onClick;
		},
		[
			isAppLinkAuthError,
			onAuthenticateApplink,
			getHrefForSmartCard,
			onClick,
			isRestrictedPageError,
			isHandleRestrictedPageOnClickEnabled,
		],
	);

	const onClickConfluencePage = useCallback(
		(page: ConfluencePage | FailedRemoteLink): typeof onClick =>
			(event) => {
				if (!isLinkedPagesBugFixEnabled) {
					event.preventDefault();
				}
				trackEmbeddedConfluence({
					action: 'clicked',
					eventName: 'openLinkedConfluencePage',
					projectType: projectContext.data?.projectType,
				});
				return onClickSmartCard(page)?.(event);
			},
		[
			isLinkedPagesBugFixEnabled,
			trackEmbeddedConfluence,
			projectContext.data?.projectType,
			onClickSmartCard,
		],
	);

	/**
	 * Returns a title for the passed confluence page. If the page is a failed remote app link,
	 * an error message will be shown to the user
	 * @param page is the confluence page to be connected
	 */
	const getTitleForSmartLink = useCallback(
		(page: ConfluencePage | FailedRemoteLink) => {
			if (isAppLinkAuthError(page)) {
				return formatMessage(messages.connectToConfluenceToAuthenticate);
			}

			return !isPageError(page) && page.href !== page.title ? page.title : undefined;
		},
		[isAppLinkAuthError, formatMessage],
	);

	return (
		<>
			<Provider
				{...(ff('platform.linking-platform.smart-card.hover-card-ai-summaries') && {
					product: smartCardProviderProduct,
					isAdminHubAIEnabled: isAIFeatureEnabled,
				})}
			>
				{pages.map((page) => (
					<SmartLinkCard
						key={page.id}
						analyticsLocation={JiraSmartLinkLocations.CONFLUENCE_PAGES}
						url={getHrefForSmartCard(page)}
						onClick={onClickConfluencePage(page)}
						canDelete
						onDeleteClick={onDeleteClick}
						text={getTitleForSmartLink(page)}
						linkId={isPageError(page) ? page.link.globalId : page.globalId}
						smartLinkId={page.id}
						page={isLinkedPagesBugFixEnabled ? getPageWithHostname(page) : page}
						loadConfluenceInNewTab={
							isLinkedPagesBugFixEnabled ? loadConfluenceInNewTab(page) : false
						}
					/>
				))}
			</Provider>
			<ConfirmationModal
				modalOpen={modalOpen}
				onCancelDelete={onCancelDelete}
				onConfirmDelete={onConfirmDelete}
			/>
		</>
	);
};
